import {
  COURT_FINDER_PAGE,
  getCityCourtsPageUrlWithSport,
  getCountryIndexPageUrlWithSport,
  getCountrySubdivisionIndexPageUrlWithSport,
} from 'constants/pages';
import { SportsEnum } from 'types/generated/client';
import { BreadcrumbItem } from './components';
import { CourtPageVenue } from './types';

// Sport segment for sport-scoped SEO URLs (breadcrumbs, city courts links) —
// mirrors the primarySport derivation the SEO branch added to V1.
export const getPrimarySportEnum = (venue: CourtPageVenue): SportsEnum =>
  venue.hasPickleball
    ? SportsEnum.Pickleball
    : venue.hasTennis
    ? SportsEnum.Tennis
    : venue.hasPadel
    ? SportsEnum.Padel
    : SportsEnum.Pickleball;

export const getPrimarySport = (venue: CourtPageVenue): string =>
  getPrimarySportEnum(venue).toLowerCase();

export const generateBreadcrumbs = (venue: CourtPageVenue): BreadcrumbItem[] => {
  const country = venue.city?.countrySubdivision?.country;
  const region = venue.city?.countrySubdivision;
  const city = venue.city;
  const sport = getPrimarySport(venue);

  const breadcrumbs: (BreadcrumbItem | false)[] = [
    { label: 'Clubs', url: COURT_FINDER_PAGE },
    !!country?.id &&
      !!country?.slug && {
        label: country.id.toUpperCase(),
        url: getCountryIndexPageUrlWithSport(country.slug, sport),
      },
    !!region?.name &&
      !!region?.slug && {
        label: region.name,
        url: getCountrySubdivisionIndexPageUrlWithSport(region.slug, sport),
      },
    !!city?.name &&
      !!city?.slug && {
        label: city.name,
        url: getCityCourtsPageUrlWithSport(city.slug, sport),
      },
    { label: venue.title, url: '', isActivePage: true },
  ];

  return breadcrumbs.filter(Boolean) as BreadcrumbItem[];
};

export const generateMeta = ({
  venueName,
  cityName,
  courtCount,
  hasTennis,
  hasPadel,
  hasPickleball,
}: {
  venueName: string;
  cityName: string;
  courtCount: number;
  hasTennis: boolean;
  hasPadel: boolean;
  hasPickleball: boolean;
}) => {
  const sports = [];
  if (hasPickleball) {
    sports.push('Pickleball');
  }
  if (hasTennis) {
    sports.push('Tennis');
  }
  if (hasPadel) {
    sports.push('Padel');
  }
  const sportName = sports.join(', ');
  const metaTitle = `${venueName} (${sportName})`;
  const metaDescription = `Play ${sportName.toLowerCase()} at ${venueName}. ${venueName} is a facility in ${cityName} with ${
    courtCount === 1 ? '1 court' : `${courtCount} courts`
  }.`;
  return {
    metaTitle,
    metaDescription,
  };
};

// Hidden, crawlable intro that reflects every sport the venue offers.
export const generateSeoIntro = (venue: CourtPageVenue, locationShortName: string) => {
  const venueSports = [
    venue.hasPickleball && 'Pickleball',
    venue.hasTennis && 'Tennis',
    venue.hasPadel && 'Padel',
  ].filter(Boolean) as string[];
  const venueSportsLabel = venueSports.length ? venueSports.join(', ') : 'Pickleball';

  return `Play ${venueSportsLabel.toLowerCase()} at ${venue.title}${
    locationShortName ? ` in ${locationShortName}` : ''
  }. Browse venue amenities, local events, and ways to play with the community near you.`;
};
